"use client";

/**
 * Custom hook for managing News page data
 * Provides data fetching and management for the News page components
 */

import { useEffect, useState } from "react";
import { NewsItem } from "@/types/newsType";
import { api, getApiUrl } from "@/api";

export interface NewsPageData {
  carouselNews: NewsItem[];
  allNews: NewsItem[];
  loading: boolean;
  error: string | null;
}

export const useNewsPageData = () => {
  // Transform NewsItem to NewsDisplayItem
  const transformNewsItem = (newsItem: NewsItem): NewsItem => {
    return {
      id: newsItem.id,
      title: newsItem.title,
      createdAt: newsItem.createdAt,
      img: newsItem.img,
      updatedAt: newsItem.updatedAt,
      content: newsItem.content,
      category: newsItem.category,
      status: newsItem.status,
      isHot: newsItem.isHot,
    };
  };

  const [data, setData] = useState<NewsPageData>({
    carouselNews: [],
    allNews: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    const loadNewsData = async () => {
      try {
        setData((prev) => ({ ...prev, loading: true, error: null }));

        // Fetch all news data using direct API call
        console.log("Fetching news data...");
        const response = await api.get<NewsItem[]>("landing-news", {
          params: {
            limit: 50,
            sort: "-createdAt",
          },
          revalidate: 60,
        });

        console.log("News API response:", response);

        if (response.success && response.data) {
          // Transform data for display
          const displayNews = response.data.map(transformNewsItem);

          // Filter hot news for carousel (keep as NewsItem)
          const carouselNews = response.data.filter(
            (newsItem) => newsItem.isHot
          );

          setData((prev) => ({
            ...prev,
            carouselNews,
            allNews: displayNews,
            loading: false,
            error: null,
          }));
        } else {
          setData((prev) => ({
            ...prev,
            loading: false,
            error:
              response.error?.message ||
              "Không thể tải dữ liệu tin tức. Vui lòng thử lại sau.",
          }));
        }
      } catch (error) {
        console.error("Error loading news data:", error);
        setData((prev) => ({
          ...prev,
          loading: false,
          error: "Không thể tải dữ liệu tin tức. Vui lòng thử lại sau.",
        }));
      }
    };

    loadNewsData();
  }, []);

  const refreshData = async () => {
    try {
      const response = await api.get<NewsItem[]>("landing-news", {
        params: {
          limit: 50,
          sort: "-createdAt",
        },
        revalidate: 60,
      });

      if (response.success && response.data) {
        const displayNews = response.data.map(transformNewsItem);
        const carouselNews = response.data.filter((newsItem) => newsItem.isHot);

        setData((prev) => ({
          ...prev,
          carouselNews,
          allNews: displayNews,
          error: null,
        }));
      }
    } catch (error) {
      console.error("Error refreshing news data:", error);
    }
  };

  return {
    ...data,
    refreshData,
  };
};
